import type { ContainerRecord, RetreatSession } from "@retreat/core";
import { createChildLogger } from "@retreat/shared";
import { STORAGE_KEYS, SESSION_STATUS } from "../constants";

const log = createChildLogger("sessionRepository");

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function startSession(retreatId: string, retreatName: string, totalScheduledSeconds?: number): RetreatSession {
  const session: RetreatSession = {
    id: generateId(),
    retreatId,
    retreatName,
    startedAt: Date.now(),
    endedAt: null,
    status: SESSION_STATUS.IN_PROGRESS,
    containers: [],
    ...(totalScheduledSeconds != null ? { totalScheduledSeconds } : {}),
  };
  try {
    const { containers: _c, ...header } = session;
    localStorage.setItem(STORAGE_KEYS.SESSION_PREFIX + session.id, JSON.stringify(header));
    localStorage.setItem(STORAGE_KEYS.CONTAINERS_PREFIX + session.id, JSON.stringify([]));
    localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION_ID, session.id);
    log.info('Session started', { sessionId: session.id, retreatId, retreatName });
  } catch {
    log.warn('localStorage quota exceeded — session in memory only', { sessionId: session.id });
  }
  return session;
}

export function appendContainer(sessionId: string, record: ContainerRecord): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CONTAINERS_PREFIX + sessionId);
    const containers: ContainerRecord[] = raw ? JSON.parse(raw) : [];
    containers.push(record);
    localStorage.setItem(STORAGE_KEYS.CONTAINERS_PREFIX + sessionId, JSON.stringify(containers));
    log.debug('Container appended', {
      sessionId,
      containerName: record.containerName,
      actualSeconds: record.actualDurationSeconds,
      scheduledSeconds: record.scheduledDurationSeconds,
      isComplete: record.isComplete,
      totalContainers: containers.length,
    });
  } catch {
    log.warn('localStorage quota exceeded — container not persisted', { sessionId, containerName: record.containerName });
  }
}

export function endSession(sessionId: string, status: 'completed' | 'incomplete'): Omit<RetreatSession, 'containers'> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SESSION_PREFIX + sessionId);
    if (!raw) {
      log.warn('endSession: session not found in localStorage', { sessionId, status });
      return null;
    }
    const session = JSON.parse(raw);
    session.status = status;
    session.endedAt = Date.now();
    localStorage.setItem(STORAGE_KEYS.SESSION_PREFIX + sessionId, JSON.stringify(session));
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_SESSION_ID);
    const durationMs = session.endedAt - session.startedAt;
    log.info('Session ended', { sessionId, status, durationMs });
    return session;
  } catch (err) {
    log.warn('endSession: failed to persist end state', { sessionId, status, error: String(err) });
    return null;
  }
}

export function getSessionHeader(sessionId: string): Omit<RetreatSession, 'containers'> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SESSION_PREFIX + sessionId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function getActiveSessionId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_SESSION_ID);
  } catch {
    return null;
  }
}

export function getAllSessions(): RetreatSession[] {
  const sessions: RetreatSession[] = [];
  try {
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(STORAGE_KEYS.SESSION_PREFIX)) continue;
      try {
        const sessionId = key.slice(STORAGE_KEYS.SESSION_PREFIX.length);
        const sessionRaw = localStorage.getItem(key);
        const containersRaw = localStorage.getItem(STORAGE_KEYS.CONTAINERS_PREFIX + sessionId);
        if (!sessionRaw) continue;
        const session = JSON.parse(sessionRaw) as RetreatSession;
        if (!session.id || !Number.isFinite(session.startedAt)) {
          log.warn('getAllSessions: skipping corrupt entry', { key });
          continue;
        }
        session.containers = containersRaw ? JSON.parse(containersRaw) : [];
        sessions.push(session);
      } catch (err) {
        log.warn('getAllSessions: failed to parse session entry', { key, error: String(err) });
      }
    }
  } catch (err) {
    log.warn('getAllSessions: localStorage unavailable', { error: String(err) });
  }
  sessions.sort((a, b) => b.startedAt - a.startedAt);
  log.trace('getAllSessions', { count: sessions.length });
  return sessions;
}

/**
 * Remove all local session data on user switch.
 *
 * Clears session records AND schedule keys so that probe() in useLifecycleBoot
 * finds nothing to resume — preventing the race where probe() reads stale data
 * before onAuthStateChange fires and clears it.
 */
export function clearLocalSessionData(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith(STORAGE_KEYS.SESSION_PREFIX) || k.startsWith(STORAGE_KEYS.CONTAINERS_PREFIX))) {
        keysToRemove.push(k);
      }
    }
    for (const k of keysToRemove) localStorage.removeItem(k);
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.LAST_USED_SCHEDULE);
    localStorage.removeItem(STORAGE_KEYS.SCHEDULED_RETREAT);
    log.info('clearLocalSessionData: cleared', { count: keysToRemove.length });
  } catch (err) {
    log.warn('clearLocalSessionData: localStorage unavailable', { error: String(err) });
  }
}
